// LocationInspectionDetails.js
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { pdf } from "@react-pdf/renderer";
import { FormattedMessage, useIntl } from "react-intl";

import {
  selectStyles,
  getNowLocalDate,
  buildSelectOptionsFromLocations,
  formatRoDateTime,
} from "../../util/common";

import * as StorageService from "../../services/StorageServices";
import { LOCALSTORAGE_ITEMS, TYPES } from "../../util/Constants";

import Spinner from "../../components/Spinner";
import StatusBadge from "../../components/StatusBadge";
import IconBadge from "../service/components/IconBadge";
import { IconClipboardList, IconFileText, IconUser } from "../../components/Icons";
import { makeSelectStylesGreenChecks, greenCheckComponents } from "../../util/selectGreenChecks";

import LocationInspectionFormPdfTemplate from "../../components/PdfTemplates/LocationInspectionFormPdfTemplate";

import {
  createLocationInspectionFormHandler,
  editLocationInspectionFormByIdHandler,
  getLocationInspectionFormByIdHandler,
  getCompanyWorkersHandler,
  getInspectionUserLocationsHandler,
} from "../../handlers/locationInspectionHandlers/HandlerLocationInspectionForms";

import {
  SECTIONS,
  ANEXA_GROUPS,
  CONCLUSION_KEYS,
  EXTRA_OBSERVATION_KEYS,
  EXISTA_OPTIONS,
  GRADE_LEGEND,
  MAX_SCORE,
  INSPECTION_STATUSES,
  anexaExistaKey,
  anexaObsKey,
  computeStatus,
  computeTotals,
} from "./inspectionForm.schema";

const toDateInputValue = (isoValue) => {
  if (!isoValue) return "";
  const d = new Date(isoValue);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const toIsoDate = (dateInputValue) =>
  dateInputValue ? new Date(`${dateInputValue}T12:00:00`).toISOString() : null;

const employeeName = (employee) =>
  employee ? `${employee.firstName || ""} ${employee.lastName || ""}`.trim() : "";

function LocationInspectionDetails(props) {
  const intl = useIntl();

  const type = StorageService.getItem(LOCALSTORAGE_ITEMS.TYPE) || "Unknown";
  const isWorker = type === TYPES.WORKER;

  const currentUserId = StorageService.getItem(LOCALSTORAGE_ITEMS.USER_ID) || null;
  const currentUserName = `${StorageService.getItem(LOCALSTORAGE_ITEMS.FIRST_NAME) || ""} ${
    StorageService.getItem(LOCALSTORAGE_ITEMS.LAST_NAME) || ""
  }`.trim();

  const companyId = props.companyId;
  const isNew = !props.documentId;

  const selectStylesGreenChecks = useMemo(() => makeSelectStylesGreenChecks(selectStyles), []);

  const [state, setState] = useState({
    loading: true,
    saving: false,
    errorMsg: null,

    id: props.documentId || null,
    status: isNew ? INSPECTION_STATUSES.DRAFT : null,
    savedOnce: false,

    inspectorId: isNew ? currentUserId : null,
    inspectorName: isNew ? currentUserName : "",
    operator: null,
    location: null,
    inspectionDate: isNew ? getNowLocalDate() : "",

    fields: {},

    locationOptions: [],
    operatorOptions: [],
  });

  const isCancelled = state.status === INSPECTION_STATUSES.CANCELLED;
  const isReadonly = isWorker || isCancelled;

  const totals = useMemo(() => computeTotals(state.fields), [state.fields]);

  // Only workers that clock in at the selected location can be its operator.
  const visibleOperatorOptions = useMemo(() => {
    const locationId = state.location?.value;
    if (!locationId) return [];
    return state.operatorOptions.filter((o) => (o.clockingLocations || []).includes(locationId));
  }, [state.operatorOptions, state.location?.value]);

  const onLocationChange = (opt) => {
    setState((s) => {
      const selectedOperator = s.operatorOptions.find((o) => o.value === s.operator?.value);
      const keepOperator =
        !!opt?.value && (selectedOperator?.clockingLocations || []).includes(opt.value);

      return { ...s, location: opt, operator: keepOperator ? s.operator : null, errorMsg: null };
    });
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const [locationsResp, workersResp] = await Promise.all([
        getInspectionUserLocationsHandler(),
        isWorker ? Promise.resolve({ data: { options: [] } }) : getCompanyWorkersHandler(companyId),
      ]);

      const locationOptions = buildSelectOptionsFromLocations(locationsResp?.data?.items || []);
      const operatorOptions = workersResp?.data?.options || [];

      if (isNew) {
        if (cancelled) return;
        setState((s) => ({
          ...s,
          loading: false,
          locationOptions,
          operatorOptions,
          location: locationOptions.length === 1 ? locationOptions[0] : null,
        }));
        return;
      }

      const resp = await getLocationInspectionFormByIdHandler(props.documentId);
      const form = resp?.data?.form;

      if (cancelled) return;

      if (!form) {
        setState((s) => ({
          ...s,
          loading: false,
          locationOptions,
          operatorOptions,
          errorMsg: intl.formatMessage({ id: "InspectionLoadFailed" }),
        }));
        return;
      }

      const locationOption =
        locationOptions.find((o) => o.value === String(form.location?.id || "")) ||
        (form.location ? { value: String(form.location.id), label: form.location.name } : null);

      const operatorOption =
        operatorOptions.find((o) => o.value === String(form.operator?.id || "")) ||
        (form.operator ? { value: String(form.operator.id), label: employeeName(form.operator) } : null);

      setState((s) => ({
        ...s,
        loading: false,
        locationOptions,
        operatorOptions,
        id: form.id,
        status: form.status,
        inspectorId: form.inspector?.id || null,
        inspectorName: employeeName(form.inspector),
        operator: operatorOption,
        location: locationOption,
        inspectionDate: toDateInputValue(form.inspectionDate),
        fields: form.fields || {},
      }));
    };

    load().catch((err) => {
      console.error("LocationInspectionDetails load", err);
      if (cancelled) return;
      setState((s) => ({ ...s, loading: false, errorMsg: intl.formatMessage({ id: "InspectionLoadFailed" }) }));
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (key, value) => {
    if (isReadonly) return;
    setState((s) => ({ ...s, fields: { ...s.fields, [key]: value }, errorMsg: null }));
  };

  const setScore = (item, rawValue) => {
    const digits = String(rawValue).replace(/\D/g, "");
    if (digits === "") {
      setField(item.key, "");
      return;
    }
    setField(item.key, Math.min(item.weight, Number(digits)));
  };

  const buildPayload = (statusOverride) => ({
    inspectorId: state.inspectorId,
    operatorId: state.operator?.value || null,
    locationId: state.location?.value || null,
    inspectionDate: toIsoDate(state.inspectionDate),
    fields: state.fields,
    status:
      statusOverride ||
      computeStatus(state.fields, {
        inspectionDate: state.inspectionDate,
        operatorId: state.operator?.value,
        locationId: state.location?.value,
      }),
  });

  const validate = () => {
    const missing = [];
    if (!state.location?.value) missing.push("Magazin");
    if (!state.operator?.value) missing.push("Operator magazin");
    if (!state.inspectorId) missing.push("Verificator");

    if (missing.length > 0) {
      setState((s) => ({
        ...s,
        errorMsg: `${intl.formatMessage({ id: "MissingFields" })}: ${missing.join(", ")}.`,
      }));
      return false;
    }
    return true;
  };

  const applyForm = (form) => {
    // The panel stays open after a create, so tell the list it must reload
    // even if the user closes it from the panel header.
    props.onSaved?.();

    setState((s) => ({
      ...s,
      saving: false,
      errorMsg: null,
      savedOnce: true,
      id: form.id,
      status: form.status,
      inspectorId: form.inspector?.id || s.inspectorId,
      inspectorName: employeeName(form.inspector) || s.inspectorName,
      inspectionDate: toDateInputValue(form.inspectionDate) || s.inspectionDate,
      fields: form.fields || s.fields,
    }));
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setState((s) => ({ ...s, saving: true }));
    const payload = buildPayload();

    if (!state.id) {
      // POST creates the form as DRAFT (the API has no status on create).
      const resp = await createLocationInspectionFormHandler(payload);
      if (resp?.data?.error != null) {
        setState((s) => ({ ...s, saving: false, errorMsg: resp.data?.message || resp.data?.error }));
        return;
      }

      const created = resp.data.form;

      // A new form stays DRAFT, unless it was filled in completely right away.
      if (payload.status === INSPECTION_STATUSES.COMPLETED) {
        const updateResp = await editLocationInspectionFormByIdHandler(created.id, payload);
        if (updateResp?.data?.error == null && updateResp.data.form) {
          applyForm(updateResp.data.form);
          return;
        }
      }

      applyForm(created);
      return;
    }

    const resp = await editLocationInspectionFormByIdHandler(state.id, payload);
    if (resp?.data?.error != null) {
      setState((s) => ({ ...s, saving: false, errorMsg: resp.data?.message || resp.data?.error }));
      return;
    }

    props.closeRightPanel(true);
  };

  const handleCancelForm = async () => {
    if (!state.id) return;

    setState((s) => ({ ...s, saving: true }));
    const resp = await editLocationInspectionFormByIdHandler(
      state.id,
      buildPayload(INSPECTION_STATUSES.CANCELLED)
    );

    if (resp?.data?.error != null) {
      setState((s) => ({ ...s, saving: false, errorMsg: resp.data?.message || resp.data?.error }));
      return;
    }

    props.closeRightPanel(true);
  };

  const handlePrintPdf = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    try {
      const docData = {
        fields: state.fields,
        locationName: state.location?.label || "",
        operatorName: state.operator?.label || "",
        inspectorName: state.inspectorName,
        inspectionDateLabel: formatRoDateTime(state.inspectionDate),
      };

      const blob = await pdf(<LocationInspectionFormPdfTemplate data={docData} />).toBlob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error("handlePrintPdf", err);
      setState((s) => ({ ...s, errorMsg: intl.formatMessage({ id: "PdfGenerationFailed" }) }));
    }
  };

  if (state.loading) return <Spinner />;

  const hasSingleLocation = state.locationOptions.length === 1;

  const renderExistaSelect = (row) => {
    const value = state.fields[anexaExistaKey(row.key)] || "";
    const selected = EXISTA_OPTIONS.find((o) => o.value === value) || null;

    return (
      <Select
        className="select"
        options={EXISTA_OPTIONS}
        value={selected}
        onChange={(opt) => setField(anexaExistaKey(row.key), opt?.value || "")}
        isDisabled={isReadonly}
        placeholder="-"
        styles={selectStylesGreenChecks}
        components={{ ...greenCheckComponents, IndicatorSeparator: () => null }}
      />
    );
  };

  return (
    <section className="form-container">
      <div className="details-form fisa-service-details">
        <div className="form-title-and-subtitle centered">
          <div className="row title-row centered">
            <h1 className="title">FISA DE VERIFICARE MAGAZIN</h1>
          </div>

          {state.status ? <StatusBadge status={state.status} /> : null}

          {state.id ? (
            <button type="button" onClick={handlePrintPdf} className="btn btn-success" style={{ marginTop: 10 }}>
              <FormattedMessage id="Print" defaultMessage="Print" />
            </button>
          ) : null}
        </div>

        {/* ---------- HEADER ---------- */}
        <div className="form-group service-form-group">
          <div className="form-group-title-row">
            <IconBadge>
              <IconUser />
            </IconBadge>
            <div className="row form-group-title">Date verificare</div>
          </div>

          <div className="inspection-field-row two">
            <div className="inspection-field-label">Magazin</div>
            {hasSingleLocation || isReadonly ? (
              <div className="input inspection-static-value">{state.location?.label || "-"}</div>
            ) : (
              <Select
                className="select"
                options={state.locationOptions}
                value={state.location}
                onChange={onLocationChange}
                styles={selectStylesGreenChecks}
                components={{ ...greenCheckComponents, IndicatorSeparator: () => null }}
              />
            )}

            <div className="inspection-field-label">Data verificarii</div>
            <input
              className="input"
              type="date"
              value={state.inspectionDate}
              readOnly={isReadonly}
              onChange={(e) => setState((s) => ({ ...s, inspectionDate: e.target.value, errorMsg: null }))}
            />
          </div>

          <div className="inspection-field-row two">
            <div className="inspection-field-label">Verificatorul magazinului,</div>
            <input className="input" value={state.inspectorName} readOnly />

            <div className="inspection-field-label">functia</div>
            <input
              className="input"
              placeholder="ex. ASM"
              value={state.fields.verificatorFunctie || ""}
              readOnly={isReadonly}
              onChange={(e) => setField("verificatorFunctie", e.target.value)}
            />
          </div>

          <div className="inspection-field-row">
            <div className="inspection-field-label">Operator magazin,</div>
            {isReadonly ? (
              <div className="input inspection-static-value">{state.operator?.label || "-"}</div>
            ) : (
              <Select
                className="select"
                options={visibleOperatorOptions}
                value={state.operator}
                onChange={(opt) => setState((s) => ({ ...s, operator: opt, errorMsg: null }))}
                placeholder={state.location ? "Alege operatorul" : "Alege intai magazinul"}
                styles={selectStylesGreenChecks}
                components={{ ...greenCheckComponents, IndicatorSeparator: () => null }}
              />
            )}
          </div>
        </div>

        {/* ---------- SCORED SECTIONS ---------- */}
        {SECTIONS.map((section) => (
          <div className="form-group service-form-group" key={section.key}>
            <div className="form-group-title-row">
              <IconBadge>
                <IconClipboardList />
              </IconBadge>
              <div className="row form-group-title">
                {`${section.no}. ${section.title}`} — {totals.sectionTotals[section.key]} / {section.max}
              </div>
            </div>

            {section.items.map((item) => (
              <div
                className="row"
                key={item.key}
                style={{ alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}
              >
                <p style={{ margin: 0, flex: "1 1 320px", fontSize: 13 }}>
                  <b>{item.no}</b> {item.label}
                </p>
                <span style={{ whiteSpace: "nowrap" }}>Pondere {item.weight}</span>
                <input
                  className="input"
                  type="text"
                  inputMode="numeric"
                  maxLength={String(item.weight).length}
                  style={{ width: 50, textAlign: "center" }}
                  value={state.fields[item.key] ?? ""}
                  readOnly={isReadonly}
                  onChange={(e) => setScore(item, e.target.value)}
                />
              </div>
            ))}

            <div className="field-row" style={{ marginTop: 12 }}>
              <label className="label">Observatii</label>
              <textarea
                className="input"
                rows={2}
                value={state.fields[section.observationsKey] || ""}
                readOnly={isReadonly}
                onChange={(e) => setField(section.observationsKey, e.target.value)}
              />
            </div>
          </div>
        ))}

        {/* ---------- SCORE SUMMARY ---------- */}
        <div className="form-group service-form-group">
          <div className="form-group-title-row">
            <IconBadge>
              <IconFileText />
            </IconBadge>
            <div className="row form-group-title">SCOR FINAL</div>
          </div>

          <div className="row" style={{ alignItems: "center", gap: 20, marginTop: 10, flexWrap: "wrap" }}>
            <span>
              <b>Obtinut:</b> {totals.total} / {MAX_SCORE}
            </span>
            <span>
              <b>Procent obtinut din total%:</b> {totals.percentLabel}
            </span>
            <span>
              <b>Nota:</b> {totals.grade}
            </span>
          </div>

          <p className="form-info-text" style={{ marginTop: 8, fontSize: 14, color: "#374151" }}>
            {`*Nota maxima este de ${MAX_SCORE} de puncte. Gradul de conformare: `}
            {GRADE_LEGEND.map((g) => `${g.label} ${g.range}`).join(", ")}.
          </p>
        </div>

        {/* ---------- CONCLUSIONS ---------- */}
        <div className="form-group service-form-group">
          <div className="form-group-title-row">
            <IconBadge>
              <IconFileText />
            </IconBadge>
            <div className="row form-group-title">Constatari si concluzii, recomandari</div>
          </div>

          {CONCLUSION_KEYS.map((key, idx) => (
            <div className="row" key={key} style={{ alignItems: "center", gap: 10, marginTop: 8 }}>
              <span style={{ width: 16 }}>{idx + 1}</span>
              <input
                className="input"
                style={{ flex: 1 }}
                value={state.fields[key] || ""}
                readOnly={isReadonly}
                onChange={(e) => setField(key, e.target.value)}
              />
            </div>
          ))}
        </div>

        {/* ---------- ANEXA ---------- */}
        <div className="form-group service-form-group">
          <div className="form-group-title-row">
            <IconBadge>
              <IconClipboardList />
            </IconBadge>
            <div className="row form-group-title">ANEXA LA FISA DE VERIFICARE MAGAZIN</div>
          </div>

          {ANEXA_GROUPS.map((group) => (
            <div key={group.key} style={{ marginTop: 14 }}>
              <div className="row" style={{ fontWeight: 600, marginBottom: 6 }}>
                {group.title}
              </div>

              {group.rows.map((row) => (
                <div
                  className="row"
                  key={row.key}
                  style={{ alignItems: "center", gap: 10, marginTop: 8, flexWrap: "wrap" }}
                >
                  <p style={{ margin: 0, flex: "1 1 320px", fontSize: 13 }}>{row.label}</p>

                  <div style={{ width: 150 }}>{renderExistaSelect(row)}</div>

                  <input
                    className="input"
                    type={row.secondType === "date" ? "date" : "text"}
                    style={{ width: 190 }}
                    placeholder={row.secondType === "date" ? undefined : group.secondColumn}
                    value={state.fields[anexaObsKey(row.key)] || ""}
                    readOnly={isReadonly}
                    onChange={(e) => setField(anexaObsKey(row.key), e.target.value)}
                  />
                </div>
              ))}
            </div>
          ))}

          <div className="field-row" style={{ marginTop: 14 }}>
            <label className="label">Observatii suplimentare</label>
            {EXTRA_OBSERVATION_KEYS.map((key) => (
              <textarea
                key={key}
                className="input"
                rows={2}
                style={{ marginTop: 6 }}
                value={state.fields[key] || ""}
                readOnly={isReadonly}
                onChange={(e) => setField(key, e.target.value)}
              />
            ))}
          </div>
        </div>

        <div className="row buttons-container">
          <button onClick={() => props.closeRightPanel(state.savedOnce)} className="btn btn-default">
            <FormattedMessage id="Close" defaultMessage="Close" />
          </button>

          {!isReadonly ? (
            <button onClick={handleSubmit} className="btn btn-success" disabled={state.saving}>
              <FormattedMessage id="Save" defaultMessage="Save" />
            </button>
          ) : null}

          {!isReadonly && state.id && state.status !== INSPECTION_STATUSES.CANCELLED ? (
            <button onClick={handleCancelForm} className="btn btn-default" disabled={state.saving}>
              <FormattedMessage id="CancelForm" defaultMessage="CancelForm" />
            </button>
          ) : null}
        </div>

        {state.errorMsg ? <div className="error">&#9888;&nbsp;{state.errorMsg}</div> : null}
      </div>
    </section>
  );
}

export default LocationInspectionDetails;
